import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import NoImg from '../assets/images/no-image-icon-23485.png';
import Navbar from './Navbar';
import Footer from './Footer';
import starGreen from "../assets/icons/StarGreen.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Place {
  id: string;
  name: string;
  Rate: number;
  ImgName: string;
  address?: string;
  opening_hours: { all_day?: string | null } | string | null;
  description: string;
  category: string;
  image_urls?: string[];
  review_count: number;
  city?: string;
}


const fetchImageFileUrls = async (placeId: string): Promise<string[]> => {
  try {
    const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
    if (!res.ok) throw new Error("Failed to fetch images");
    const data = await res.json();

    return (data.data?.images || data.images || []).map((img: any) => img.file_url);
  } catch {
    return [];
  }
};


const fetchPlacesBySearch = async (searchQuery: string, page: number, pageSize: number) => {
  const url = `${BASE_URL}/api/places/?search=${encodeURIComponent(searchQuery)}&page=${page}&page_size=${pageSize}`;
  console.log('Fetching URL:', url);
  
  const res = await fetch(url);
  if (!res.ok) throw new Error(`خطا در جستجو: ${res.status}`);
  
  const json = await res.json();
  console.log('API Response:', json);
  
  const dataArray = json.data || [];
  
  const formatted: Place[] = await Promise.all(
    dataArray.map(async (item: any) => {
      const imageUrls = await fetchImageFileUrls(item.place_id);
      const categoryName = item.categories?.length > 0 ? item.categories[0].name : "عمومی";
      

      let openingHoursObj = { all_day: null };
      if (item.opening_hours) {
        if (typeof item.opening_hours === 'string') {
          openingHoursObj = { all_day: item.opening_hours };
        } else {
          openingHoursObj = item.opening_hours;
        }
      }
      
      return {
        id: item.place_id,
        name: item.name,
        Rate: item.avg_rating || item.rate || 0,
        ImgName: item.name,
        address: item.address || '',
        opening_hours: openingHoursObj,
        description: item.description || '',
        category: categoryName,
        image_urls: imageUrls,
        review_count: item.review_count || 0,
        city: item.cities?.[0]?.name || '',
      };
    })
  );
  
  return {
    places: formatted,
    totalPages: json.pagination ? Math.ceil(json.pagination.count / pageSize) : 1,
    count: json.pagination?.count || 0,
  };
};


const getImageUrl = (fileUrl?: string) => fileUrl ? `${BASE_URL}${fileUrl}` : NoImg;

const PlaceCard: React.FC<{ place: Place }> = ({ place }) => {
  const image = place.image_urls?.[0];
  const showRating = place.Rate > 0;
  const ratingValue = place.Rate % 1 === 0 ? place.Rate.toFixed(0) : place.Rate.toFixed(1);

  return (
    <Link to={`/places/${place.id}`} className="block group">
      <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300 flex p-3 gap-3 h-full">
        {/* سمت چپ: عکس */}
        <div className="relative w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
          <img
            src={getImageUrl(image)}
            alt={place.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              (e.target as HTMLImageElement).src = NoImg;
            }}
          />
        </div>

        {/* سمت راست: اطلاعات */}
        <div className="flex flex-col justify-between flex-1">
          <div className="flex flex-col justify-between items-start gap-2 mb-2">
            <h3 className="text-sm font-myYekanDemibold text-gray-800 flex-1 line-clamp-2" dir="auto">
              {place.name}
            </h3>
            <div className="flex items-center gap-2">
              {place.review_count > 0 && (
                <span className="text-xs text-gray-500">
                  ({place.review_count})
                </span>
              )}
              {showRating && (
                <div className="flex gap-1 text-xs text-[#038545] font-myYekanFaNumDemiBold">
                  <span>{ratingValue}</span>
                  <img src={starGreen} className="w-3 h-3" alt="rating" />
                </div>
              )}
            </div>
          </div>

          {/* {place.city && (
            <div className="text-xs text-gray-500 mb-1" dir="auto">
              {place.city}
            </div>
          )} */}

          {place.address && (
            <div className="flex items-center text-xs text-gray-500" dir="auto">
              <MapPinIcon className="h-3 w-3 ml-1 text-gray-400 flex-shrink-0" />
              <span className="line-clamp-1 font-myYekanFaNumMedium">{place.address}</span>
            </div>
          )}

          {place.opening_hours && typeof place.opening_hours === 'object' && place.opening_hours.all_day && (
            <div className="flex items-center text-xs mt-1 text-gray-500">
              <ClockIcon className="h-3 w-3 ml-1 text-gray-400 flex-shrink-0" />
              <span className="line-clamp-1">{place.opening_hours.all_day}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// کامپوننت کارت موبایل
const MobilePlaceCard: React.FC<{ place: Place; isLast: boolean }> = ({ place, isLast }) => {
  const image = place.image_urls?.[0];
  const showRating = place.Rate > 0;
  const ratingValue = place.Rate % 1 === 0 ? place.Rate.toFixed(0) : place.Rate.toFixed(1);

  return (
    <Link to={`/places/${place.id}`} className="block">
      <div className={`py-4 ${!isLast ? 'border-b border-gray-200' : ''}`}>
        <div className="flex gap-3">
          <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
            <img
              src={getImageUrl(image)}
              alt={place.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = NoImg;
              }}
            />
          </div>
          
          <div className="flex flex-col justify-between flex-1">
            <div className="flex flex-col justify-between items-start gap-1 mb-1">
              <h2 className="text-sm font-myYekanDemibold text-gray-800 line-clamp-1 flex-1" dir="auto">
                {place.name}
              </h2>
              <div className="flex items-center gap-2">
                {place.review_count > 0 && (
                  <span className="text-xs text-gray-500">
                    ({place.review_count})
                  </span>
                )}
                {showRating && (
                  <div className="flex gap-1 text-xs text-[#038545] font-myYekanFaNumMedium">
                    <span>{ratingValue}</span>
                    <img src={starGreen} className="w-3 h-3" alt="rating" />
                  </div>
                )}
              </div>
            </div>

            {place.address && (
              <div className="flex items-center text-xs text-gray-500" dir="auto">
                <MapPinIcon className="h-3 w-3 ml-1 text-gray-400 flex-shrink-0" />
                <span className="line-clamp-1 font-myYekanFaNumMedium">{place.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

// کامپوننت اسکلت دسکتاپ
const DesktopPlaceCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg animate-pulse p-3">
      <div className="flex gap-3">
        <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    </div>
  );
};

// کامپوننت اسکلت موبایل
const MobilePlaceCardSkeleton: React.FC = () => {
  return (
    <div className="py-4 border-b border-gray-200 animate-pulse">
      <div className="flex gap-3">
        <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
};

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    if (searchQuery) {
      const savedPage = sessionStorage.getItem(`search_page_${searchQuery}`);
      if (savedPage) {
        setCurrentPage(Number(savedPage));
      } else {
        setCurrentPage(1);
      }
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem(`search_page_${searchQuery}`, String(currentPage));
    }
  }, [currentPage, searchQuery]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['search', searchQuery, currentPage],
    queryFn: () => fetchPlacesBySearch(searchQuery, currentPage, pageSize),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!searchQuery,
  });

  const places = data?.places || [];
  const totalPages = data?.totalPages || 1;
  const count = data?.count || 0;

  const handlePageChange = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const max = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + max - 1);
    
    if (end - start < max - 1) {
      start = Math.max(1, end - max + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (!searchQuery) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-32 text-red-600">
          <p className="text-lg">موردی برای جستجو مشخص نشده است</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-myYekanFaNumRegular">
      <Navbar />
      <div className="site-container pb-6 pt-6 tablet:pt-14 desktop:pt-20">
        <div className="mb-6 mt-12 border-b pb-4 tablet:mt-0">
          <h1 className="text-xl font-myYekanDemibold text-gray-800">
            نتایج جستجو برای: <span className="text-[#038545]">"{searchQuery}"</span>
          </h1>
          {!isLoading && !isError && (
            <p className="text-sm text-gray-500 mt-1">
              {count} مورد پیدا شد
            </p>
          )}
        </div>
        
        <div className="w-full">
          {isLoading ? (
            <>
              {/* اسکلتون برای دسکتاپ */}
              <div className="hidden lg:grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: pageSize }).map((_, i) => (
                  <DesktopPlaceCardSkeleton key={i} />
                ))}
              </div>
              {/* اسکلتون برای موبایل */}
              <div className="lg:hidden space-y-0">
                {Array.from({ length: 5 }).map((_, i) => (
                  <MobilePlaceCardSkeleton key={i} />
                ))}
              </div>
            </>
          ) : isError ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{(error as Error).message}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-[#038545] text-white rounded-lg hover:bg-[#068646] transition"
              >
                تلاش مجدد
              </button>
            </div>
          ) : (
            <>
              {places.length > 0 ? (
                <>
                  {/* نمایش برای دسکتاپ */}
                  <div className="hidden lg:grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {places.map((place) => (
                      <PlaceCard key={place.id} place={place} />
                    ))}
                  </div>
                  
                  {/* نمایش برای موبایل */}
                  <div className="lg:hidden space-y-0">
                    {places.map((place, index) => (
                      <MobilePlaceCard key={place.id} place={place} isLast={index === places.length - 1} />
                    ))}
                  </div>
                  
{totalPages > 1 && (
  <div className="mt-10 flex justify-center">
    <nav className="flex items-center gap-1" dir="rtl">
      {/* دکمه صفحه قبل (در RTL این فلش راست است) */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-2 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
        aria-label="صفحه قبل"
      >
        <ChevronRightIcon className="h-5 w-5" />
      </button>
      
      {/* شماره صفحات */}
      {getPageNumbers().map(pageNum => (
        <button
          key={pageNum}
          onClick={() => handlePageChange(pageNum)}
          className={`min-w-[40px] h-10 px-3 rounded-md font-myYekanFaNumRegular transition ${
            currentPage === pageNum
              ? 'bg-[#038545] text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
          }`}
        >
          {pageNum}
        </button>
      ))}
      
      {/* دکمه صفحه بعد (در RTL این فلش چپ است) */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-2 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
        aria-label="صفحه بعد"
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </button>
    </nav>
  </div>
)}
                </>
              ) : (
                <div className="text-center py-20 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 text-lg">هیچ موردی با عبارت "{searchQuery}" یافت نشد.</p>
                  <p className="text-gray-400 text-sm mt-2">لطفاً عبارت دیگری را جستجو کنید.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SearchResults;
